import type { ColorRangeParameters } from '../core/color-range';
import type { ToneSettings } from '../filters/tone';

interface WorkerResponse {
  taskId: string;
  generation: number;
  type: 'progress' | 'result' | 'cancelled' | 'error';
  progress?: number;
  channels?: ArrayBuffer;
  pixels?: ArrayBuffer;
  coverage?: ArrayBuffer;
  message?: string;
}

type AnalysisPayload =
  | { type: 'histogram'; pixels: ArrayBuffer }
  | { type: 'tone'; pixels: ArrayBuffer; settings: ToneSettings; strength: number }
  | { type: 'color-range'; pixels: ArrayBuffer; width: number; parameters: ColorRangeParameters };

let worker: Worker | null = null;
let generation = 0;
let cancelledTasks = 0;
const requests = new Map<string, {
  generation: number;
  resolve: (response: WorkerResponse) => void;
  reject: (reason: Error) => void;
  onProgress?: (progress: number) => void;
}>();

function analysisWorker(): Worker {
  worker ??= new Worker(new URL('./analysis.worker.ts', import.meta.url), { type: 'module', name: 'maipo-analysis' });
  worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
    const pending = requests.get(event.data.taskId);
    if (!pending || pending.generation !== event.data.generation) return;
    if (event.data.type === 'progress') {
      pending.onProgress?.(event.data.progress ?? 0);
      return;
    }
    requests.delete(event.data.taskId);
    if (event.data.type === 'cancelled') pending.reject(new DOMException('Operation cancelled.', 'AbortError'));
    else if (event.data.type === 'error') pending.reject(new Error(event.data.message ?? 'The analysis worker failed.'));
    else pending.resolve(event.data);
  };
  worker.onerror = () => {
    for (const pending of requests.values()) pending.reject(new Error('The image-analysis worker failed.'));
    requests.clear();
    worker?.terminate();
    worker = null;
  };
  return worker;
}

function requestWorker(payload: AnalysisPayload, signal?: AbortSignal, onProgress?: (progress: number) => void): Promise<WorkerResponse> {
  if (signal?.aborted) return Promise.reject(new DOMException('Operation cancelled.', 'AbortError'));
  const taskGeneration = ++generation;
  const taskId = `analysis:${taskGeneration}:${Date.now().toString(36)}`;
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      cancelledTasks += 1;
      analysisWorker().postMessage({ type: 'cancel', taskId, generation: taskGeneration });
    };
    signal?.addEventListener('abort', onAbort, { once: true });
    requests.set(taskId, {
      generation: taskGeneration,
      resolve: (response) => {
        signal?.removeEventListener('abort', onAbort);
        resolve(response);
      },
      reject: (error) => {
        signal?.removeEventListener('abort', onAbort);
        reject(error);
      },
      ...(onProgress ? { onProgress } : {}),
    });
    analysisWorker().postMessage({ ...payload, taskId, generation: taskGeneration }, { transfer: [payload.pixels] });
  });
}

function transferablePixels(pixels: Uint8ClampedArray): ArrayBuffer {
  return pixels.buffer.slice(pixels.byteOffset, pixels.byteOffset + pixels.byteLength);
}

export async function computeHistogram(pixels: Uint8ClampedArray, signal?: AbortSignal, onProgress?: (progress: number) => void): Promise<Float64Array> {
  const response = await requestWorker({ type: 'histogram', pixels: transferablePixels(pixels) }, signal, onProgress);
  if (!response.channels) throw new Error('Histogram worker returned no channel data.');
  return new Float64Array(response.channels);
}

export async function previewTone(pixels: Uint8ClampedArray, settings: ToneSettings, strength: number, signal?: AbortSignal, onProgress?: (progress: number) => void): Promise<Uint8ClampedArray> {
  const response = await requestWorker({ type: 'tone', pixels: transferablePixels(pixels), settings, strength }, signal, onProgress);
  if (!response.pixels) throw new Error('Adjustment worker returned no pixel data.');
  return new Uint8ClampedArray(response.pixels);
}

/**
 * Computes Color Range coverage off the main thread. Cancellable through
 * `signal`, which the dialog uses to discard superseded slider positions.
 */
export async function computeColorRange(
  pixels: Uint8ClampedArray,
  width: number,
  parameters: ColorRangeParameters,
  signal?: AbortSignal,
  onProgress?: (progress: number) => void,
): Promise<Uint8Array> {
  const response = await requestWorker({ type: 'color-range', pixels: transferablePixels(pixels), width, parameters }, signal, onProgress);
  if (!response.coverage) throw new Error('Color Range worker returned no coverage data.');
  return new Uint8Array(response.coverage);
}

export function analysisDiagnostics(): { activeTasks: number; cancelledTasks: number } {
  return { activeTasks: requests.size, cancelledTasks };
}
